/**
 * Ingredient-Vendor Association API Client
 * Frontend API calls for managing vendor associations per ingredient
 */

import { apiCall } from '../api';
import type {
  IngredientVendor,
  IngredientVendorsResponse,
  IngredientVendorResponse,
  AddIngredientVendorRequest,
  UpdateIngredientVendorRequest,
} from '@/types/api';

/**
 * Fetch all vendor associations for an ingredient
 */
export async function fetchIngredientVendors(
  ingredientId: number
): Promise<IngredientVendor[]> {
  const response = await apiCall<IngredientVendorsResponse>(
    `/api/ingredients/${ingredientId}/vendors`,
    { method: 'GET' }
  );
  return response.ingredientVendors;
}

/**
 * Add a vendor association to an ingredient
 */
export async function addIngredientVendor(
  ingredientId: number,
  data: AddIngredientVendorRequest
): Promise<IngredientVendor> {
  const response = await apiCall<IngredientVendorResponse>(
    `/api/ingredients/${ingredientId}/vendors`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  return response.ingredientVendor;
}

/**
 * Update a vendor association for an ingredient
 */
export async function updateIngredientVendor(
  ingredientId: number,
  vendorId: number,
  data: UpdateIngredientVendorRequest
): Promise<IngredientVendor> {
  const response = await apiCall<IngredientVendorResponse>(
    `/api/ingredients/${ingredientId}/vendors/${vendorId}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
  return response.ingredientVendor;
}

/**
 * Remove a vendor association from an ingredient
 */
export async function removeIngredientVendor(
  ingredientId: number,
  vendorId: number
): Promise<void> {
  await apiCall<{ message: string }>(
    `/api/ingredients/${ingredientId}/vendors/${vendorId}`,
    { method: 'DELETE' }
  );
}
